<template>
  <div class="fee-combo-manage-container">
    <el-card class="search-card">
      <div slot="header" class="flex justify-between">
        <span>{{ $t('feeComboManage.search.title') }}</span>
      </div>
      <el-row :gutter="20">
        <el-col :span="6">
          <el-input v-model.trim="searchForm.comboName" :placeholder="$t('feeComboManage.search.comboName')" clearable />
        </el-col>
        <el-col :span="4">
          <el-button type="primary" @click="handleSearch">
            {{ $t('common.search') }}
          </el-button>
          <el-button @click="handleReset">
            {{ $t('feeComboManage.search.reset') }}
          </el-button>
        </el-col>
      </el-row>
    </el-card>

    <el-card class="list-card">
      <div slot="header" class="flex justify-between">
        <span>{{ $t('feeComboManage.list.title') }}</span>
        <el-button type="primary" size="small" @click="handleAdd">
          {{ $t('common.add') }}
        </el-button>
      </div>

      <el-table v-loading="loading" :data="tableData" border style="width: 100%" @row-click="handleSelectCombo">
        <el-table-column prop="comboName" :label="$t('feeComboManage.table.comboName')" align="center" min-width="160" />
        <el-table-column prop="remark" :label="$t('feeComboManage.table.remark')" align="center" min-width="200" />
        <el-table-column prop="createTime" :label="$t('feeComboManage.table.createTime')" align="center" width="180" />
        <el-table-column :label="$t('common.operation')" align="center" width="220">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click.stop="handleEdit(scope.row)">
              {{ $t('common.edit') }}
            </el-button>
            <el-button size="mini" type="success" @click.stop="handleSelectCombo(scope.row)">
              {{ $t('feeComboManage.table.member') }}
            </el-button>
            <el-button size="mini" type="danger" @click.stop="handleDelete(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination :current-page.sync="page.current" :page-sizes="[10, 20, 30, 50]" :page-size="page.size"
        :total="page.total" layout="total, sizes, prev, pager, next, jumper" @size-change="handleSizeChange"
        @current-change="handleCurrentChange" />
    </el-card>

    <el-card v-if="selectedCombo.comboId" class="member-card">
      <div slot="header" class="flex justify-between">
        <span>{{ selectedCombo.comboName }} - {{ $t('feeComboManage.member.title') }}</span>
        <el-button type="primary" size="small" @click="handleAddMember">
          {{ $t('feeComboManage.member.add') }}
        </el-button>
      </div>

      <el-table v-loading="memberLoading" :data="memberData" border style="width: 100%">
        <el-table-column prop="feeTypeCdName" :label="$t('feeComboManage.member.feeType')" align="center" width="120" />
        <el-table-column prop="feeName" :label="$t('feeComboManage.member.feeName')" align="center" min-width="160" />
         <el-table-column prop="feeFlagName" :label="$t('feeComboManage.member.feeFlag')" align="center" width="120" />
         <el-table-column prop="billTypeName" :label="$t('feeComboManage.member.billType')" align="center" width="120" />
        <el-table-column prop="paymentCycle" :label="$t('feeComboManage.member.paymentCycle')" align="center" width="120" />
        <el-table-column prop="computingFormulaText" :label="$t('feeComboManage.member.formula')" align="center" min-width="160" />
        <el-table-column prop="squarePrice" :label="$t('feeComboManage.member.price')" align="center" width="120" />
        <el-table-column :label="$t('common.operation')" align="center" width="100">
          <template slot-scope="scope">
            <el-button size="mini" type="danger" @click="handleDeleteMember(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <add-fee-combo ref="addDialog" @success="handleSuccess" />
    <edit-fee-combo ref="editDialog" @success="handleSuccess" />
    <delete-fee-combo ref="deleteDialog" @success="handleDeleteSuccess" />
    <add-fee-combo-member ref="addMemberDialog" @success="handleMemberSuccess" />
    <delete-fee-combo-member ref="deleteMemberDialog" @success="handleMemberSuccess" />
  </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import { listFeeCombo } from '@/api/fee/feeComboManageApi'
import { listFeeComboMember } from '@/api/fee/feeComboMemberManageApi'
import AddFeeCombo from '@/components/fee/addFeeCombo'
import EditFeeCombo from '@/components/fee/editFeeCombo'
import DeleteFeeCombo from '@/components/fee/deleteFeeCombo'
import AddFeeComboMember from '@/components/fee/addFeeComboMember'
import DeleteFeeComboMember from '@/components/fee/deleteFeeComboMember'

export default {
  name: 'FeeComboManageList',
  components: {
    AddFeeCombo,
    EditFeeCombo,
    DeleteFeeCombo,
    AddFeeComboMember,
    DeleteFeeComboMember
  },
  data() {
    return {
      loading: false,
      memberLoading: false,
      searchForm: {
        comboName: '',
        communityId: ''
      },
      tableData: [],
      memberData: [],
      selectedCombo: {},
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.getList()
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        this.searchForm.communityId = getCommunityId()
        const params = {
          page: this.page.current,
          row: this.page.size,
          ...this.searchForm
        }
        const { data, total } = await listFeeCombo(params)
        this.tableData = data || []
        this.page.total = total || 0
      } catch (error) {
        this.$message.error(this.$t('feeComboManage.fetchError'))
      } finally {
        this.loading = false
      }
    },
    async getMemberList() {
      if (!this.selectedCombo.comboId) {
        this.memberData = []
        return
      }
      try {
        this.memberLoading = true
        const params = {
          page: 1,
          row: 500,
          comboId: this.selectedCombo.comboId,
          communityId: getCommunityId()
        }
        const { data } = await listFeeComboMember(params)
        this.memberData = data || []
      } catch (error) {
        this.$message.error(this.$t('feeComboManage.member.fetchError'))
      } finally {
        this.memberLoading = false
      }
    },
    handleSearch() {
      this.page.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm.comboName = ''
      this.handleSearch()
    },
    handleAdd() {
      this.$refs.addDialog.open()
    },
    handleEdit(row) {
      this.$refs.editDialog.open(row)
    },
    handleDelete(row) {
      this.$refs.deleteDialog.open(row)
    },
    handleSelectCombo(row) {
      this.selectedCombo = row
      this.getMemberList()
    },
    handleAddMember() {
      this.$refs.addMemberDialog.open(this.selectedCombo)
    },
    handleDeleteMember(row) {
      this.$refs.deleteMemberDialog.open(row)
    },
    handleSuccess() {
      this.getList()
    },
    handleDeleteSuccess(deletedCombo) {
      if (this.selectedCombo.comboId === deletedCombo.comboId) {
        this.selectedCombo = {}
        this.memberData = []
      }
      this.getList()
    },
    handleMemberSuccess() {
      this.getMemberList()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.getList()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.getList()
    }
  }
}
</script>

<style lang="scss" scoped>
.fee-combo-manage-container {
  padding: 20px;

  .search-card,
  .list-card,
  .member-card {
    margin-bottom: 20px;
  }

  .el-pagination {
    margin-top: 20px;
    text-align: right;
  }
}
</style>
